import moment from 'moment';
import {memo, useRef, useMemo, useEffect, useState} from 'react';
import styled from 'styled-components';
import {TextHeading10, TextHeading5} from '../../../index';
import {EventDTO} from '../../../../models/event/EventDTO';
import {deadlineUtils, Palette} from 'hubbl-shared';
import {useAppSelector, useI18n} from '../../../../hooks';
import {UserSelector} from '../../../../store/user/UserSelector';
import {TextHeading8} from '../../../index';

interface EventListCardDeadlineProps {
  event: EventDTO;
  isPast: boolean;
}

export const EventListCardDeadline = memo(
  ({event, isPast}: EventListCardDeadlineProps) => {
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const [currentTime, setCurrentTime] = useState(moment());
    const hasTriggeredCallback = useRef(false);
    const t = useI18n();

    const {
      calculateDeadlineInfo,
      createDeadlineConfig,
      shouldStartCountdown,
      getDeadlineColor,
      handleDeadlinePassedCallback,
    } = deadlineUtils;

    // Check if user has made a decision
    const hasUserResponded = useMemo(() => {
      if (!userDetails) return false;

      const isAttending =
        event.attendees?.some(
          (attendee: any) => attendee.id === userDetails.id,
        ) || false;

      const isDeclined =
        event.declinedUserIds?.includes(userDetails.id) || false;

      return isAttending || isDeclined;
    }, [userDetails, event.attendees, event.declinedUserIds]);

    // Calculate deadline info using the same logic as EventCoverDeadline
    const deadlineInfo = useMemo(() => {
      if (!event.deadline) {
        return {
          timeLeft: -1,
          isPassed: true,
          displayText: '',
          displayUnit: '',
          deadlineTime: '',
        };
      }

      const config = createDeadlineConfig(
        {
          deadline: event.deadline.toDate(),
          startDateTime: event.startDateTime.toDate(),
        },
        currentTime,
      );
      return calculateDeadlineInfo(config);
    }, [
      event.deadline,
      event.startDateTime,
      currentTime,
      createDeadlineConfig,
      calculateDeadlineInfo,
    ]);

    // Real-time countdown timer for deadline
    useEffect(() => {
      if (!event.deadline) return;

      const config = createDeadlineConfig(
        {
          deadline: event.deadline.toDate(),
          startDateTime: event.startDateTime.toDate(),
        },
        moment(),
      );
      const initialDeadlineInfo = calculateDeadlineInfo(config);

      // Only start countdown if deadline hasn't passed and we're within last 5 minutes
      if (shouldStartCountdown(initialDeadlineInfo)) {
        const interval = setInterval(() => {
          setCurrentTime(moment());
        }, 1000);

        return () => clearInterval(interval);
      }
    }, [
      event.deadline,
      event.startDateTime,
      createDeadlineConfig,
      calculateDeadlineInfo,
      shouldStartCountdown,
    ]);

    // Handle deadline passed callback
    useEffect(() => {
      handleDeadlinePassedCallback(
        deadlineInfo,
        () => {
          // Refresh deadline info when deadline passes
        },
        hasTriggeredCallback,
      );
    }, [deadlineInfo, deadlineInfo.isPassed, handleDeadlinePassedCallback]);

    // Format deadline text based on time remaining - matching EventCoverDeadline logic
    const getDeadlineText = (deadlineInfo: any) => {
      if (!deadlineInfo || deadlineInfo.isPassed) {
        return t.events.deadlinePassed;
      }

      if (
        deadlineInfo.displayUnit === 'DAYS' ||
        deadlineInfo.displayUnit === 'DAY'
      ) {
        const days = parseInt(deadlineInfo.displayText, 10);
        if (isNaN(days)) {
          return t.events.rsvpToday;
        }
        if (days === 0) return t.events.rsvpToday;
        if (days === 1) return t.events.rsvpTomorrow;
        return t.events.rsvpInDays.replace('{days}', days.toString());
      } else if (
        deadlineInfo.displayUnit === 'HOURS' ||
        deadlineInfo.displayUnit === 'HOUR'
      ) {
        const hours = parseInt(deadlineInfo.displayText, 10);
        if (isNaN(hours)) {
          return t.events.rsvpToday;
        }
        if (hours === 1) return t.events.rsvpInOneHour;
        return t.events.rsvpInHours.replace('{hours}', hours.toString());
      } else if (
        deadlineInfo.displayUnit === 'MINUTES' ||
        deadlineInfo.displayUnit === 'MINUTE'
      ) {
        const minutes = parseInt(deadlineInfo.displayText, 10);
        if (isNaN(minutes)) {
          return t.events.rsvpToday;
        }
        if (minutes === 1) return t.events.rsvpInOneMinute;
        return t.events.rsvpInMinutes.replace('{minutes}', minutes.toString());
      } else {
        // For seconds, show live countdown
        return t.events.rsvpInSeconds.replace(
          '{seconds}',
          deadlineInfo.displayText,
        );
      }
    };

    // Determine what to show - only deadline-related info now
    const getDisplayInfo = () => {
      // If user has already responded, don't show deadline info
      if (hasUserResponded) {
        return null;
      }

      // Check if there was a deadline and if it has passed
      if (event.deadline) {
        if (deadlineInfo.isPassed) {
          // Past events that had a deadline
          return {
            text: `⏰  ${t.events.deadlinePassed}`,
            color: Palette.almostBlack250,
          };
        } else {
          // Active deadline (future events)
          return {
            text: `⏰  ${getDeadlineText(deadlineInfo)}`,
            color: getDeadlineColor(deadlineInfo),
          };
        }
      } else {
        // No deadline was set
        if (isPast) {
          // Past events with no deadline
          return {
            text: t.events.noDeadline,
            color: Palette.white,
          };
        } else {
          // Future events with no deadline
          return {
            text: t.events.noDeadline,
            color: Palette.white,
          };
        }
      }
    };

    const displayInfo = getDisplayInfo();

    // Don't render anything if user has responded or no display info
    if (!displayInfo) {
      return null;
    }

    // For "No deadline" cases, render as small text instead of button
    if (displayInfo.text === t.events.noDeadline) {
      return (
        <NoDeadlineText>
          <TextHeading8 text={displayInfo.text} />
        </NoDeadlineText>
      );
    }

    return (
      <DeadlineContainer>
        <DeadlineTag color={displayInfo.color || Palette.primaryPadua}>
          <TextHeading5 text={displayInfo.text} />
        </DeadlineTag>
        <DeadlineTimeText>
          <TextHeading10
            color={Palette.almostBlack500}
            text={deadlineInfo.deadlineTime}
          />
        </DeadlineTimeText>
      </DeadlineContainer>
    );
  },
);

const DeadlineContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DeadlineTag = styled.div<{color: string}>`
  padding: 0.5rem 0.75rem;
  background: ${props => props.color};
  border-radius: 12px;
  border: 0.5 solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  font-weight: 600;
  color: white;
  font-size: 0.85rem;
  max-width: 200px;
  text-align: center;
`;

const DeadlineTimeText = styled.div`
  margin-top: 4px;
  color: ${Palette.almostBlack250};
  font-size: 0.75rem;
  opacity: 0.8;
  text-align: right;
`;

const NoDeadlineText = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  padding: 0.25rem 0.5rem;
  color: ${Palette.almostBlack250};
  font-size: 0.75rem;
  opacity: 0.7;
`;
